// ATTICUS-ANALYTICS-COMMAND-CENTER-1 — Compliance / CCO lens.
//
// Litigation rate (firm-wide claim view) + PHI access volume & audit activity
// (admin/compliance gated). Honest deferred cards for reopened-rate, statutory
// deadlines, denied-PHI attempts, and file-QA scores.
import React, { useEffect, useState, useMemo } from "react";
import { apiFetch } from "../../api";
import { KpiCard, ChartCard, buildQS } from "../AnalyticsKit";
import { Trend, HBar, Bullet, TONE_HEX } from "../charts";

export default function ComplianceLens({ filters, openDrill, reloadKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    apiFetch(`/analytics/compliance?${buildQS(filters)}`)
      .then((d) => { if (live) { setData(d); setLoading(false); } })
      .catch((e) => { if (live) { setError(e.message || String(e)); setLoading(false); } });
    return () => { live = false; };
  }, [filters, reloadKey]);

  const lit = data?.litigation;
  const phi = data?.phi_access;
  const litPct = lit ? (lit.rate * 100).toFixed(1) : null;
  const recordTypes = useMemo(() => (phi?.by_record_type || []).map((r) => ({ name: r.type, value: r.count })), [phi]);
  const audit = useMemo(() => (data?.audit_activity || []).map((r) => ({ name: r.action, value: r.count })), [data]);
  const def = data?.deferred || {};

  if (error) return <div className="an-error">Couldn't load compliance analytics: {error}</div>;
  const L = loading && !data;
  const adminOff = data && !data.admin_section_available;

  return (
    <div className="an-stack">
      <div className="an-kpis" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>
        <KpiCard label="Litigation Rate" tone="amber"
                 value={L ? "…" : (lit ? `${litPct}%` : "—")}
                 sub={lit ? `${lit.in_litigation} of ${lit.open_total} open` : "no access"}
                 onClick={lit ? () => openDrill("litigation", null, "Open claims in litigation") : undefined} />
        <KpiCard label="In Litigation" value={L ? "…" : (lit ? num(lit.in_litigation) : "—")} sub="open claims"
                 onClick={lit ? () => openDrill("litigation", null, "Open claims in litigation") : undefined} />
        <KpiCard label="PHI Accesses" tone="blue" value={L ? "…" : (phi ? num(phi.total) : "—")}
                 sub={phi ? "in period" : "admin only"} />
        <KpiCard label="Audit Events" value={L ? "…" : (data?.audit_activity ? num(sum(audit)) : "—")}
                 sub={data?.audit_activity ? "in period (excl. bulk)" : "admin only"} />
      </div>

      <div className="an-grid">
        <ChartCard title="Litigation Rate" subtitle="open claims in litigation" loading={L}
                   unavailable={!L && !lit ? { status: "Restricted", reason: "Requires firm-wide claim access.", dependency: "VIEW_ALL_CLAIMS" } : undefined}>
          {lit && (
            <div style={{ padding: "18px 6px" }}>
              <div style={{ fontSize: 34, fontWeight: 700, color: "var(--navy)", textAlign: "center", marginBottom: 14 }}>{litPct}%</div>
              <Bullet value={lit.rate} target={0.15} tone={lit.rate > 0.15 ? "red" : "green"} label={`${lit.in_litigation}/${lit.open_total}`} />
              <div style={{ fontSize: 11, color: "var(--text3)", textAlign: "center", marginTop: 10 }}>marker = 15% reference</div>
            </div>
          )}
        </ChartCard>

        <ChartCard title="PHI Access by Record Type" subtitle="admin/compliance" loading={L}
                   unavailable={adminOff ? { status: "Restricted", reason: "PHI access analytics are admin/compliance-only.", dependency: "VIEW_SYSADMIN_PANEL" } : undefined}
                   empty={phi && !recordTypes.length}>
          {phi && <HBar data={recordTypes} color="#3b6fd4" />}
        </ChartCard>

        <ChartCard title="PHI Access Volume" subtitle="reads & writes per day" wide loading={L}
                   unavailable={adminOff ? { status: "Restricted", reason: "PHI access analytics are admin/compliance-only.", dependency: "VIEW_SYSADMIN_PANEL" } : undefined}
                   empty={phi && !phi.series?.length}>
          {phi && (
            <Trend data={phi.series || []} xKey="day" area={false}
                   series={[{ key: "read", label: "Reads", color: "#3b6fd4" },
                            { key: "write", label: "Writes", color: "#ef4444" }]} />
          )}
        </ChartCard>

        <ChartCard title="Audit Activity by Type" subtitle="excludes bulk import/backfill events" wide loading={L}
                   unavailable={adminOff ? { status: "Restricted", reason: "Audit analytics are admin/compliance-only.", dependency: "VIEW_SYSADMIN_PANEL" } : undefined}
                   empty={data?.audit_activity && !audit.length}>
          {data?.audit_activity && <HBar data={audit} color="#6366f1" height={Math.max(260, audit.length * 24)} />}
        </ChartCard>

        <ChartCard title="Reopened Claims Rate"
                   unavailable={{ status: "Deferred", reason: def.reopened_rate, dependency: "reopen-transition tracking" }} />
        <ChartCard title="Statutory Deadline Violations"
                   unavailable={{ status: "Deferred", reason: def.statutory_deadlines, dependency: "SOL tracking" }} />
        <ChartCard title="Denied PHI Access Attempts"
                   unavailable={{ status: "Deferred", reason: def.denied_phi, dependency: "denial-event capture" }} />
        <ChartCard title="File QA / Compliance Score"
                   unavailable={{ status: "Deferred", reason: def.file_qa, dependency: "QA review scores" }} />
      </div>
    </div>
  );
}

function num(n) { return n == null ? "—" : Number(n).toLocaleString(); }
function sum(arr) { return (arr || []).reduce((a, b) => a + (b.value || 0), 0); }
